import {
  DataTable,
  formatDataToLabelValue,
  MainButton,
  STATUSPAGE,
} from '@repo/ui';
import { useEffect, useState } from 'react';
import { Card, CardBody, Col, Row } from 'reactstrap';

import noDataBg from '../../../assets/images/no-task.png';
import { ModalBannerForm } from '../../Entities/components/entity-settings/tabs-contents/advertising-tab-content/components/modal-banner-form';
import { makeAdvertisingColumns } from '../columns';
import { useAdvertising } from '../hooks/useAdvertising';

export const AdvertisingPage = () => {
  const {
    formik,
    entityId,
    isModalOpen,
    listCourses,
    imagePreview,
    advertisingCreateStatus,
    advertisingListStatus,
    advertisingList: apiAdvertisingList,
    handleClose,
    setIsModalOpen,
    handleImageChange,
    validateAndProcessImage,
    requestCourseList,
    requestListAdvertising,
    requestDeleteAdvertising,
    requestUpdateAdvertising,
  } = useAdvertising();

  const [advertisingList, setAdvertisingList] = useState<
    Array<{
      id: string;
      photo: string;
      linkUrl: string;
      course: Array<{
        id: string;
        name: string;
      }>;
      showInStudentApp: boolean;
      showInProfessionalApp: boolean;
    }>
  >([]);

  // Constants
  const noDataConfig = {
    noDataTitle: 'Nenhum banner por aqui ainda',
    noDataDescription:
      'Adicione banners para divulgar cursos, campanhas e links nos aplicativos.',
  };

  // Atualiza o estado local quando os dados da API chegarem

  const handleUpdateBanner = (
    bannerId: string,
    updatedFields: {
      linkUrl?: string;
      courseIds?: string[];
      showInStudentApp?: boolean;
      showInProfessionalApp?: boolean;
      photo?: string;
    },
  ) => {
    const banner = advertisingList.find((b) => b.id === bannerId);
    if (!banner) return;

    setAdvertisingList((prevList) =>
      prevList.map((item) =>
        item.id === bannerId
          ? {
              ...item,
              ...updatedFields,
              course: updatedFields.courseIds
                ? updatedFields.courseIds.map((id) => {
                    const existingCourse = item.course.find((c) => c.id === id);
                    return existingCourse || { id, name: 'Curso Selecionado' };
                  })
                : item.course,
            }
          : item,
      ),
    );

    const currentCourseIds = banner.course?.map((c) => c.id);

    requestUpdateAdvertising({
      advertisementID: bannerId,
      organizationHashId: entityId || '',
      photo: updatedFields.photo ?? null,
      linkUrl: updatedFields.linkUrl ?? banner.linkUrl,
      courseIds: updatedFields.courseIds ?? currentCourseIds,
      showInStudentApp:
        updatedFields.showInStudentApp ?? banner.showInStudentApp,
      showInProfessionalApp:
        updatedFields.showInProfessionalApp ?? banner.showInProfessionalApp,
    });
  };

  const handleToggleChange = (
    bannerId: string,
    field: 'showInStudentApp' | 'showInProfessionalApp',
    value: boolean,
  ) => {
    handleUpdateBanner(bannerId, { [field]: value });
  };

  const handleLinkChange = (bannerId: string, value: string) => {
    handleUpdateBanner(bannerId, { linkUrl: value });
  };

  const handleCoursesChange = (bannerId: string, courseIds: string[]) => {
    handleUpdateBanner(bannerId, { courseIds: courseIds });
  };

  const handleBannerImageChange = (bannerId: string, file: File) => {
    validateAndProcessImage(file, (base64String) => {
      handleUpdateBanner(bannerId, { photo: base64String });
    });
  };

  useEffect(() => {
    requestCourseList();
    requestListAdvertising({ organizationId: entityId });
  }, [entityId]);

  useEffect(() => {
    // Sincroniza sempre, inclusive quando a API retornar lista vazia
    setAdvertisingList(apiAdvertisingList ?? []);
  }, [apiAdvertisingList]);

  return (
    <>
      <Card className="mt-5">
        <CardBody>
          <Row className="mb-3">
            <Col xs={12} className="d-flex justify-content-end align-items-end">
              <MainButton className="" onClick={handleClose}>
                <i className="ri-add-line align-bottom me-1"></i>
                Adicionar Banner
              </MainButton>
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <DataTable
                noDataTitle={noDataConfig.noDataTitle}
                noDataDescription={noDataConfig.noDataDescription}
                pagination
                actions={[]}
                loading={advertisingListStatus === STATUSPAGE.LOADING}
                canSearch={false}
                data={advertisingList}
                defaultRowsPerPage={10}
                handleDelete={(advertisementID) => {
                  requestDeleteAdvertising({
                    advertisementID,
                    organizationHashId: entityId,
                  });
                }}
                columns={makeAdvertisingColumns({
                  courseOptions: formatDataToLabelValue(
                    listCourses as never,
                    'id',
                    'nome',
                  ),
                  onToggleChange: handleToggleChange,
                  onLinkChange: handleLinkChange,
                  onCoursesChange: handleCoursesChange,
                  onImageChange: handleBannerImageChange,
                })}
              />
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Modal de Criação/Edição */}
      <ModalBannerForm
        formik={formik}
        isOpen={isModalOpen}
        handleClose={handleClose}
        imagePreview={imagePreview}
        isLoading={advertisingCreateStatus}
        handleImageChange={handleImageChange}
        toggle={() => setIsModalOpen(!isModalOpen)}
        courseOptions={formatDataToLabelValue(
          listCourses as never,
          'id',
          'nome',
        )}
      />
    </>
  );
};
